import { createFileRoute, Link } from "@tanstack/react-router";
import { SiteNav } from "@/components/site/SiteNav";
import { Footer } from "@/components/site/Sections";
import {
  Layout,
  CheckCircle2,
  ArrowRight,
  ChevronRight,
  Sparkles,
  BarChart3,
  TrendingUp,
  Target,
  Users,
  Award,
  Globe,
  Clock,
  ShieldCheck,
  Check,
  Building2,
  FileCheck,
  Layers,
  Search,
  Zap,
  Eye,
  MousePointerClick,
  Monitor,
  Smartphone,
  Maximize2,
  Sparkle,
} from "lucide-react";

export const Route = createFileRoute("/services_/display-native-ads")({
  head: () => ({
    meta: [
      {
        title:
          "Display, Native & Rich Media Ads — KMWAdvertising · GDN, DV360, Taboola & Outbrain",
      },
      {
        name: "description",
        content:
          "Display Advertising Services That Increase Brand Visibility Across the Web. Programmatic Display, DV360, Native Ads (Taboola/Outbrain) & Interactive Rich Media by KMWA LLP.",
      },
      {
        property: "og:title",
        content: "Display, Native & Rich Media Ads — KMWAdvertising",
      },
      {
        property: "og:description",
        content:
          "Deliver Advertising That Blends Naturally & Interactive HTML5 Experiences. High-impact banner & native content strategy.",
      },
    ],
  }),
  component: DisplayNativeAdsServicePage,
});

export function DisplayNativeAdsServicePage() {
  const displayPlatforms = [
    "Google Display Network (GDN)",
    "Programmatic Display Advertising",
    "DV360 (Display & Video 360)",
    "Premium Publisher Networks",
    "Mobile App Advertising",
    "Responsive Display Ads",
    "HTML5 Banner Ads",
    "Interactive Display Ads",
    "Dynamic Display Campaigns (DCO)",
    "Remarketing Display Campaigns",
  ];

  const displayProcessSteps = [
    {
      num: "01",
      title: "Audience Research & Targeting",
      desc: "Analyzing demographics, interests, browsing behavior, purchase intent, geographic location, device usage, and 1st-party audience data to build precise target personas.",
      icon: Users,
    },
    {
      num: "02",
      title: "Creative Strategy & Production",
      desc: "Developing high-impact static banners, animated HTML5 ads, responsive display units, and Dynamic Creative Optimisation (DCO) tailored to every screen size.",
      icon: Layout,
    },
    {
      num: "03",
      title: "Campaign Optimisation",
      desc: "Continuous refinement through publisher placement optimization, bid adjustments, frequency capping, creative A/B testing, and conversion tracking.",
      icon: Zap,
    },
  ];

  const nativePlatforms = [
    "Taboola Discovery Platform",
    "Outbrain Native Network",
    "Google Discovery Ads",
    "Yahoo Native Network",
    "Premium News Websites",
    "Top Business Publications",
    "Lifestyle & Entertainment Portals",
    "Financial News Platforms",
    "Industry-Specific Verticals",
  ];

  const nativeFormats = [
    {
      title: "In-Feed Native Ads",
      desc: "Advertisements integrated directly within website content feeds, offering a seamless and non-disruptive browsing experience.",
      icon: Layers,
    },
    {
      title: "Content Recommendation Widgets",
      desc: "Recommended articles and sponsored stories displayed alongside editorial content to drive qualified website traffic.",
      icon: Search,
    },
    {
      title: "Sponsored Articles",
      desc: "Long-form branded editorial content designed to educate audiences while building trust, authority, and thought leadership.",
      icon: FileCheck,
    },
    {
      title: "Native Video Advertising",
      desc: "Video content naturally embedded within publisher websites and digital news feeds.",
      icon: Eye,
    },
  ];

  const richMediaFormats = [
    {
      title: "Expandable Banner Ads",
      desc: "Interactive advertisements that expand on hover or click, providing rich canvas details without leaving the webpage.",
      icon: Maximize2,
    },
    {
      title: "Interactive HTML5 Ads",
      desc: "Responsive creatives featuring custom animations, swipe gestures, image galleries, and interactive product tours.",
      icon: Monitor,
    },
    {
      title: "Video Rich Media Ads",
      desc: "Combining HD video content with clickable calls-to-action, product showcases, and interactive overlays.",
      icon: Sparkles,
    },
    {
      title: "Interactive Product Showcases",
      desc: "Allowing users to explore products directly within the ad unit through sliders, hotspots, carousels, and 360-degree views.",
      icon: Target,
    },
    {
      title: "Gamified Advertising",
      desc: "Interactive experiences boosting engagement through quizzes, mini-games, spin-to-win mechanics, and personalized brand interactions.",
      icon: Sparkle,
    },
  ];

  const displayBenefits = [
    "Massive audience reach across millions of websites",
    "Increased brand awareness and visual presence",
    "Advanced audience & contextual targeting",
    "Cost-effective CPM campaigns",
    "Cross-device desktop & mobile visibility",
    "High-intent remarketing opportunities",
    "Strong visual storytelling",
    "Scalable campaign performance",
  ];

  const nativeBenefits = [
    "Increase content engagement organically",
    "Improve click-through rates (CTR)",
    "Generate highly qualified website traffic",
    "Build thought leadership & brand authority",
    "Reduce banner blindness among readers",
    "Strengthen brand credibility in news environments",
    "Support content marketing & SEO strategies",
    "Enhance customer education",
  ];

  const richMediaBenefits = [
    "Higher engagement & dwell rates",
    "Increased click-through rates (CTR)",
    "Superior brand recall and message retention",
    "Longer interaction time with ad units",
    "Enhanced visual storytelling canvas",
    "Improved product demonstrations",
    "Higher conversion potential",
    "Stronger overall customer experience",
  ];

  return (
    <div className="dark relative min-h-screen bg-background text-foreground flex flex-col w-full overflow-x-clip">
      <SiteNav />

      <main className="flex-1 w-full overflow-x-clip pt-24 md:pt-28">
        {/* HERO SECTION */}
        <section className="relative overflow-hidden py-14 sm:py-20 md:py-24 border-b border-white/10">
          <div className="absolute inset-0 grid-bg opacity-15" aria-hidden />
          <div className="glow-orb -left-32 top-10 h-[450px] w-[450px] bg-[var(--glow-purple)] opacity-25" />
          <div className="glow-orb right-[-100px] top-20 h-[400px] w-[400px] bg-[var(--glow-cyan)] opacity-25" />

          <div className="container-x relative z-10">
            {/* Breadcrumb */}
            <div className="flex flex-wrap items-center gap-2.5 text-xs uppercase tracking-[0.2em] text-muted-foreground mb-6">
              <Link to="/" className="hover:text-white transition">
                Home
              </Link>
              <ChevronRight className="h-3 w-3 text-muted-foreground" />
              <Link to="/services" className="hover:text-white transition">
                Services
              </Link>
              <ChevronRight className="h-3 w-3 text-muted-foreground" />
              <span className="text-[var(--glow-purple)] font-semibold">Display, Native & Rich Media Ads</span>
            </div>

            <div className="max-w-4xl">
              <div className="inline-flex items-center gap-2 glass rounded-full px-4 py-1.5 text-xs uppercase tracking-[0.2em] text-muted-foreground border border-white/10 mb-6">
                <Layout className="h-3.5 w-3.5 text-[var(--glow-purple)]" />
                Digital Media Capabilities
              </div>

              <h1 className="font-display text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold leading-[1.12] tracking-tight text-white">
                Display Advertising Services That Increase Brand Visibility Across the Web
              </h1>

              <p className="mt-4 text-base font-mono text-white/80 uppercase tracking-widest">
                Display Ads, Native Ads & Rich Media Campaigns
              </p>

              <p className="mt-4 text-lg sm:text-xl font-accent italic text-[var(--glow-purple)] font-medium border-l-2 border-[var(--glow-purple)] pl-4 py-1">
                Increase Brand Visibility Across the Web with High-Impact Visuals, Seamless Native Content & Interactive Rich Media
              </p>

              <div className="mt-6 space-y-4 text-base sm:text-lg text-white/90 font-normal leading-relaxed">
                <p>
                  Display advertising is one of the most effective ways to build brand awareness, attract new audiences and nurture potential customers throughout the buying journey. By placing visually engaging banner advertisements across websites, mobile applications and digital platforms, businesses can reach consumers wherever they spend their time online.
                </p>
                <p className="text-sm sm:text-base text-muted-foreground leading-relaxed">
                  Unlike search advertising, which targets users actively searching for products or services, display advertising introduces your brand to new audiences before they begin searching, making it an essential component of a full-funnel marketing strategy.
                </p>
                <p className="text-sm sm:text-base text-muted-foreground leading-relaxed">
                  At <strong className="text-white">Kris Mediaworks Advertising LLP</strong>, we create data-driven display, native, and rich media campaigns that combine audience intelligence, compelling creative and advanced media buying across GDN, DV360, Programmatic exchanges, Taboola, and Outbrain to deliver measurable business outcomes.
                </p>
              </div>

              <div className="mt-8 flex flex-wrap gap-4">
                <a href="/contact-us" className="btn-hero !py-3.5 !px-7">
                  Request Display Strategy Proposal
                  <ArrowRight className="h-4 w-4" />
                </a>
                <Link to="/services" className="btn-ghost !py-3.5 !px-7">
                  Explore All Services
                </Link>
              </div>
            </div>
          </div>
        </section>

        {/* DISPLAY ADVERTISING OVERVIEW & PROCESS */}
        <section className="py-14 sm:py-20 border-b border-white/10 bg-white/[0.01]">
          <div className="container-x">
            <div className="max-w-3xl mb-12">
              <span className="text-xs font-mono uppercase tracking-widest text-[var(--glow-purple)] block mb-2">
                Programmatic & GDN Execution
              </span>
              <h2 className="font-display text-2xl sm:text-3xl md:text-4xl font-bold text-white">
                Our Display Advertising Solutions
              </h2>
            </div>

            <div className="rounded-3xl glass-strong border border-white/15 p-6 sm:p-10 md:p-12 mb-16 relative overflow-hidden shadow-2xl">
              <div className="glow-orb -top-32 -right-32 h-96 w-96 rounded-full bg-[var(--glow-purple)] opacity-15" />

              <div className="grid lg:grid-cols-12 gap-8 items-center relative z-10">
                <div className="lg:col-span-5 space-y-4">
                  <span className="text-xs font-mono uppercase tracking-widest text-[var(--glow-purple)] block">
                    Ad Exchanges & DV360
                  </span>
                  <h3 className="font-display text-2xl sm:text-3xl font-bold text-white">
                    Display Network Coverage
                  </h3>
                  <p className="text-xs sm:text-sm text-muted-foreground leading-relaxed">
                    We develop and manage display campaigns across premium advertising exchanges, ensuring high viewability, brand safety, and algorithmic audience targeting.
                  </p>
                </div>

                <div className="lg:col-span-7 bg-[#06060c]/90 rounded-2xl p-6 border border-white/10">
                  <h4 className="text-xs font-mono uppercase tracking-widest text-white/80 mb-4 block">
                    Supported Platforms & Formats:
                  </h4>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    {displayPlatforms.map((dp, i) => (
                      <div key={i} className="flex items-center gap-2.5 p-2.5 rounded-xl bg-white/[0.03] border border-white/5 text-xs text-white/90">
                        <Check className="h-3.5 w-3.5 text-emerald-400 shrink-0" />
                        <span>{dp}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {/* PROCESS STEPS */}
            <div className="grid md:grid-cols-3 gap-6">
              {displayProcessSteps.map((step, idx) => {
                const SIcon = step.icon;
                return (
                  <div key={idx} className="p-6 sm:p-8 rounded-2xl glass-strong border border-white/10 hover:border-white/20 transition duration-300 flex flex-col justify-between">
                    <div>
                      <div className="flex items-center justify-between mb-4">
                        <span className="text-xs font-mono text-[var(--glow-purple)] uppercase font-bold">{step.num}</span>
                        <div className="h-10 w-10 rounded-xl bg-white/5 border border-white/10 flex items-center justify-center text-white">
                          <SIcon className="h-5 w-5 text-[var(--glow-purple)]" />
                        </div>
                      </div>
                      <h4 className="font-display text-lg font-bold text-white mb-2">{step.title}</h4>
                      <p className="text-xs sm:text-sm text-muted-foreground leading-relaxed">{step.desc}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        {/* NATIVE ADVERTISING CAMPAIGNS */}
        <section className="py-16 md:py-24 border-b border-white/10 relative">
          <div className="container-x">
            <div className="max-w-3xl mb-12">
              <span className="text-xs font-mono uppercase tracking-widest text-sky-400 block mb-2">
                Non-Disruptive Content Discovery
              </span>
              <h2 className="font-display text-3xl sm:text-4xl font-bold text-white">
                Native Advertising Campaigns
              </h2>
              <p className="text-sm sm:text-base text-muted-foreground mt-2 leading-relaxed">
                Deliver Advertising That Blends Naturally with Premium Content. Native ads match the design, format, and user experience of publisher platforms (Taboola, Outbrain, Discovery), improving click-through rates and reducing advertising fatigue.
              </p>
            </div>

            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
              {nativeFormats.map((nf, idx) => {
                const NIcon = nf.icon;
                return (
                  <div key={idx} className="p-6 rounded-2xl glass-strong border border-white/10 hover:border-white/20 transition duration-300 space-y-3">
                    <div className="h-10 w-10 rounded-xl bg-white/5 border border-white/10 flex items-center justify-center text-sky-400">
                      <NIcon className="h-5 w-5" />
                    </div>
                    <h4 className="font-display text-lg font-bold text-white">{nf.title}</h4>
                    <p className="text-xs text-muted-foreground leading-relaxed">{nf.desc}</p>
                  </div>
                );
              })}
            </div>

            <div className="p-8 rounded-3xl glass-strong border border-white/15 bg-[#06060c]/90">
              <h4 className="text-xs font-mono uppercase tracking-widest text-white/80 mb-4 block">
                Native Content Discovery Networks & Publisher Verticals:
              </h4>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2.5">
                {nativePlatforms.map((np, i) => (
                  <div key={i} className="p-3 rounded-xl bg-white/[0.03] border border-white/5 text-xs text-white/90 text-center font-medium">
                    {np}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* RICH MEDIA ADVERTISING CAMPAIGNS */}
        <section className="py-16 md:py-24 border-b border-white/10 relative bg-white/[0.01]">
          <div className="container-x">
            <div className="max-w-3xl mb-12">
              <span className="text-xs font-mono uppercase tracking-widest text-[var(--glow-orange)] block mb-2">
                Interactive HTML5 Experiences
              </span>
              <h2 className="font-display text-3xl sm:text-4xl font-bold text-white">
                Rich Media Advertising Campaigns
              </h2>
              <p className="text-sm sm:text-base text-muted-foreground mt-2 leading-relaxed">
                Create Interactive Digital Experiences That Capture Attention. Rich media incorporates expandable content, swipe gestures, video overlays, and gamified mechanics to encourage direct brand interaction.
              </p>
            </div>

            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 mb-16">
              {richMediaFormats.map((rm, idx) => {
                const RIcon = rm.icon;
                return (
                  <div key={idx} className="p-6 sm:p-8 rounded-2xl glass-strong border border-white/10 hover:border-white/20 transition duration-300 space-y-4">
                    <div className="h-10 w-10 rounded-xl bg-white/5 border border-white/10 flex items-center justify-center text-[var(--glow-orange)]">
                      <RIcon className="h-5 w-5" />
                    </div>
                    <h4 className="font-display text-xl font-bold text-white">{rm.title}</h4>
                    <p className="text-xs sm:text-sm text-muted-foreground leading-relaxed">{rm.desc}</p>
                  </div>
                );
              })}
            </div>

            {/* BENEFITS COMPARISON MATRIX (3 COLUMNS) */}
            <div className="border-t border-white/10 pt-12">
              <div className="max-w-3xl mb-8">
                <h3 className="font-display text-2xl font-bold text-white">
                  Comparative Strategic Benefits
                </h3>
                <p className="text-xs sm:text-sm text-muted-foreground mt-1">
                  How Display, Native, and Rich Media Advertisements support your full-funnel objectives.
                </p>
              </div>

              <div className="grid md:grid-cols-3 gap-6">
                {/* Display Ads */}
                <div className="p-6 rounded-2xl glass-strong border border-white/10 space-y-4">
                  <h4 className="font-display text-lg font-bold text-white flex items-center gap-2">
                    <Layout className="h-4 w-4 text-[var(--glow-purple)]" />
                    Display Ads Benefits
                  </h4>
                  <div className="space-y-2">
                    {displayBenefits.map((b, i) => (
                      <div key={i} className="flex items-center gap-2 text-xs text-white/90">
                        <Check className="h-3.5 w-3.5 text-[var(--glow-purple)] shrink-0" />
                        <span>{b}</span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Native Ads */}
                <div className="p-6 rounded-2xl glass-strong border border-white/10 space-y-4">
                  <h4 className="font-display text-lg font-bold text-white flex items-center gap-2">
                    <Layers className="h-4 w-4 text-sky-400" />
                    Native Ads Benefits
                  </h4>
                  <div className="space-y-2">
                    {nativeBenefits.map((b, i) => (
                      <div key={i} className="flex items-center gap-2 text-xs text-white/90">
                        <Check className="h-3.5 w-3.5 text-sky-400 shrink-0" />
                        <span>{b}</span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Rich Media Ads */}
                <div className="p-6 rounded-2xl glass-strong border border-white/10 space-y-4">
                  <h4 className="font-display text-lg font-bold text-white flex items-center gap-2">
                    <Sparkles className="h-4 w-4 text-[var(--glow-orange)]" />
                    Rich Media Benefits
                  </h4>
                  <div className="space-y-2">
                    {richMediaBenefits.map((b, i) => (
                      <div key={i} className="flex items-center gap-2 text-xs text-white/90">
                        <Check className="h-3.5 w-3.5 text-[var(--glow-orange)] shrink-0" />
                        <span>{b}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* CLOSING CTA BANNER */}
        <section className="py-20 md:py-28 relative overflow-hidden">
          <div className="glow-orb left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 h-[500px] w-[500px] bg-[var(--glow-purple)] opacity-20" />

          <div className="container-x relative z-10 text-center max-w-4xl">
            <div className="inline-flex items-center gap-2 glass rounded-full px-4 py-1.5 text-xs uppercase tracking-widest text-muted-foreground border border-white/10 mb-6">
              <Layout className="h-3.5 w-3.5 text-[var(--glow-purple)]" />
              Digital Display Advisory
            </div>

            <h2 className="font-display text-3xl sm:text-4xl md:text-5xl font-bold text-white leading-tight">
              Ready to Boost Your Digital Visibility with Display, Native & Rich Media Ads?
            </h2>

            <p className="mt-6 text-base sm:text-lg text-muted-foreground max-w-2xl mx-auto leading-relaxed">
              Connect with Kris Mediaworks Advertising LLP's display and native media specialists to build high-impact creative campaigns tailored to your target audience.
            </p>

            <div className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-4">
              <a href="/contact-us" className="btn-hero w-full sm:w-auto">
                Request Display Proposal
                <ArrowRight className="h-4 w-4" />
              </a>
              <Link to="/services" className="btn-ghost w-full sm:w-auto">
                Back to All Services
              </Link>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}
